import type { IconProps } from "@/components/types";
import type { JSX } from "react";
import {
  boxiconsSizes,
  boxiconsTypes,
  fontawesomeIconsSizes,
  iconSizeEm,
  IconSizes,
  MIN_ICON_FONT_SIZE_PX,
  type IconSources
} from "./types";

const prefixes = ["text", "color"];

// 1. Construir la RegExp con grupos de captura
// La expresión es: (\b(text|color|bg)-([^\s]+))
//   - Primer grupo (): Captura la palabra completa (ej. "text-lg")
//   - Segundo grupo (): Captura solo el prefijo (ej. "text")
//   - Tercer grupo (): Captura el resto de la palabra después del guion
const regex = new RegExp(String.raw`\b(?:${prefixes.join("|")})-([^\s]+)`, "g");
const defaultType = "solid";

/**
 * @description Atomic component to render icons from different sources (FontAwesome, BoxIcons).
 * Supports dynamic class parsing for size and styles.
 * @param {IconProps} props - Component properties.
 * @param {string} props.name - Name of the icon, can include source and type (e.g., "name:source:type").
 * @param {keyof typeof IconSizes} [props.size="md"] - Size of the icon.
 * @param {string} [props.className=""] - Additional CSS classes.
 * @returns {JSX.Element} The rendered icon element.
 */
const Icon = ({
  name,
  size = "2x",
  className = "",
  ...props
}: IconProps): JSX.Element => {
  let iconClasses = "";
  let iconName = name;
  let iconSource = "fa";
  let iconType: keyof typeof boxiconsTypes = defaultType;
  const styles = (
    Array.isArray(className) ? className.join(" ") : className
  ).trim();

  // Parse name if it includes format specifications
  if (name.includes(":")) {
    const parts = name.split(":");
    iconName = parts[0] ?? "question-mark";
    iconSource = parts[1] as IconSources;
    iconType = (parts[2] as keyof typeof boxiconsTypes) ?? defaultType;
    iconClasses = parts[3] ?? "";
  }

  // 2. Usar el método matchAll() para obtener las capturas
  // matchAll() devuelve un iterador con todos los grupos de captura.
  const matches = [...styles.matchAll(regex)];

  // 3. Procesar los resultados para obtener solo la parte deseada
  const suffixes = matches.map((match) => match[1]);
  suffixes.forEach((suffix) => {
    if (suffix && Object.keys(IconSizes).includes(suffix)) {
      size = suffix as keyof typeof IconSizes;
    }
  });

  switch (iconSource) {
    case "bx":
      iconClasses = `${iconSource}${boxiconsTypes[iconType] ?? ""} ${iconSource}-${iconName} ${iconSource}-${boxiconsSizes[size]}`;
      break;
    case "fa":
      iconClasses = `${iconSource}-${iconType} ${iconSource}-${iconName} ${iconSource}-${fontawesomeIconsSizes[size]}`;
      break;
  }

  // Only forward props that are valid on a DOM element (aria-*/data-*, event
  // handlers, and a few global attrs). Callers sometimes pass component-level
  // props such as `action` (a function) through a spread; forwarding those to
  // <i> triggers React's "Invalid value for prop ... on <i> tag" warning.
  const DOM_SAFE_KEYS = new Set(["role", "title", "id"]);
  const accessibilityProps = Object.fromEntries(
    Object.entries(props).filter(
      ([key]) =>
        key.startsWith("aria-") ||
        key.startsWith("data-") ||
        key.startsWith("on") ||
        DOM_SAFE_KEYS.has(key)
    )
  ) as Record<string, unknown>;

  // Add any additional classes
  const combinedClasses = className
    ? `${iconClasses} ${className}`
    : iconClasses;

  // Only set aria-hidden if no aria-label is provided
  if (!("aria-label" in accessibilityProps)) {
    accessibilityProps["aria-hidden"] = "true";
  }

  // Never render smaller than MIN_ICON_FONT_SIZE_PX, but keep sizes bigger
  // than that (an inline style already outranks any class-based font-size,
  // fa-*/bx-*/text-* included, without needing !important here).
  return (
    <i
      {...accessibilityProps}
      className={combinedClasses}
      style={{
        fontSize: `max(${MIN_ICON_FONT_SIZE_PX}px, ${iconSizeEm[size]}em)`
      }}
    />
  );
};

export default Icon;
